import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  fetchCurrentFiling,
  getDependents,
} from "../../../../../../api/filings/filingAPI";
import { Handler401 } from "../../../../../../utils/auth/authHandler";
import {
  getCryptoOriginDocuments,
  getDocumentList,
} from "../../../../../../api/filings/documentsAPI";
import {
  getExchangeCode,
  getExchangeValue,
} from "../../../../../../utils/crypto/cryptoHelpers";
import { cryptocurrencyExchanges } from "../../../../../../components/ui/forms/selectOptionData";
import { useRefreshOnAuth } from "../../../../../../hooks/useRefreshOnAuth";

export function useFilingData(filingUuid) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [filing, setFiling] = useState({});
  const [documents, setDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [dependents, setDependents] = useState([]);

  // Transform API data (snake_case) to frontend format (camelCase)
  // preserved exactly from your source
  const transformDependentFromAPI = useCallback((apiDependent) => {
    return {
      id: apiDependent.id,
      firstname: apiDependent.firstname,
      lastname: apiDependent.lastname,
      ssnItin: apiDependent.ssn_itin,
      dateOfBirth: apiDependent.date_of_birth,
      monthsLivedWith: String(apiDependent.months_lived_with),
      relationship:
        apiDependent.relationship === "qch"
          ? "qualifying_child"
          : apiDependent.relationship === "qre"
          ? "qualifying_relative"
          : apiDependent.relationship,
    };
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const pathFiling = "tax/filing";
      const filingResponse = await fetchCurrentFiling(
        pathFiling,
        filingUuid,
        "overview",
        { includeMetrics: true },
      );

      if (!filingResponse.ok) {
        if (filingResponse.status === 401) {
          Handler401(navigate);
          return;
        }
        throw new Error("Failed to fetch data");
      }

      const filingData = await filingResponse.json();
      setFiling(filingData);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching data:", err);
      setLoading(false);
    }
  }, [filingUuid, navigate]);

  const fetchDocuments = useCallback(async () => {
    if (!filingUuid) return;
    setDocumentsLoading(true);
    try {
      const apiUrl = "tax/documents";
      const response = await getDocumentList(apiUrl, filingUuid);

      if (response.status === 401) {
        Handler401(navigate);
        return;
      }

      let regularDocuments = [];
      if (response.ok) {
        const data = await response.json();
        regularDocuments = data.documents || [];
      }

      let cryptoDocuments = [];
      const ptfSituation = filing.tax_situation?.details?.PTF?.situation;
      const cryptoTransaction = ptfSituation?.cryptoTransaction;

      if (cryptoTransaction && cryptoTransaction.value === true) {
        const exchanges = cryptoTransaction.fields?.exchanges || [];
        const taxYears = Array.isArray(ptfSituation.taxYears)
          ? ptfSituation.taxYears
          : ptfSituation.taxYears
          ? [ptfSituation.taxYears]
          : [];
        const cryptoProcessInfo = {
          service_id: filingUuid,
          service_model: "taxfiling",
        };

        const cryptoFetchPromises = [];
        for (const exchangeValue of exchanges) {
          const exchangeCode = getExchangeCode(exchangeValue);
          if (!exchangeCode) continue;

          const exchangeOptions = cryptocurrencyExchanges();
          const exchangeConfig = exchangeOptions.find(
            (opt) => opt.value === exchangeValue?.toLowerCase(),
          );
          const usesTransactions =
            exchangeConfig && exchangeConfig.documentType === "transactions";

          if (!usesTransactions) continue;

          for (const taxYear of taxYears) {
            cryptoFetchPromises.push(
              getCryptoOriginDocuments(
                taxYear,
                exchangeCode,
                "tax",
                cryptoProcessInfo,
              ).then(async (cryptoResponse) => {
                if (cryptoResponse.ok) {
                  const cryptoData = await cryptoResponse.json();
                  const cryptoDocsArray = Array.isArray(cryptoData)
                    ? cryptoData
                    : cryptoData.results || cryptoData.documents || [];

                  return cryptoDocsArray.map((cryptoDoc) => ({
                    documentId: cryptoDoc.id,
                    documentName:
                      cryptoDoc.filename || `Crypto - ${cryptoDoc.exchange}`,
                    documentType: "CRYPTO",
                    documentYear: cryptoDoc.report_year,
                    documentSource: "yours",
                    status: "PENDING",
                    exchange:
                      getExchangeValue(cryptoDoc.exchange) ||
                      cryptoDoc.exchange?.toLowerCase(),
                    _cryptoDoc: cryptoDoc,
                    _isCryptoDocument: true,
                    _standardizedId:
                      cryptoDoc.standardized_id || cryptoDoc.standardizedId,
                  }));
                }
                return [];
              }),
            );
          }
        }
        const cryptoDocsArrays = await Promise.all(cryptoFetchPromises);
        cryptoDocuments = cryptoDocsArrays.flat();
      }

      setDocuments([...regularDocuments, ...cryptoDocuments]);
    } catch (err) {
      console.error("Error fetching documents:", err);
    } finally {
      setDocumentsLoading(false);
    }
  }, [filingUuid, filing.tax_situation, navigate]);

  const fetchDependentsData = useCallback(async () => {
    if (!filingUuid) return;
    try {
      const response = await getDependents(filingUuid);
      if (response.ok) {
        const dependentsData = await response.json();
        const transformedDependents = Array.isArray(dependentsData)
          ? dependentsData.map(transformDependentFromAPI)
          : [];
        setDependents(transformedDependents);
      } else if (response.status === 401) {
        Handler401(navigate);
      }
    } catch (err) {
      console.error("Error fetching dependents:", err);
    }
  }, [filingUuid, navigate, transformDependentFromAPI]);

  const handleDocumentUpdated = (summaryMetrics = null) => {
    // Update summary_metrics if returned from API
    if (summaryMetrics) {
      setFiling((prev) => ({
        ...prev,
        summary_metrics: summaryMetrics,
      }));
    }
    // Refresh documents after Accept/Reject action
    fetchDocuments();
  };

  // Sync logic
  useEffect(() => {
    fetchData();
  }, [filingUuid]);

  useEffect(() => {
    if (filingUuid && !loading) {
      fetchDependentsData();
    }
  }, [filingUuid, loading]);

  useEffect(() => {
    fetchDocuments();
  }, [filingUuid, loading, filing]);

  const handleAuthRefresh = useCallback(() => {
    fetchData();
    fetchDocuments();
    fetchDependentsData();
  }, [fetchData, fetchDocuments, fetchDependentsData]);

  // This side effect is used for: (a) Refresh page (b) When render the page for first
  useRefreshOnAuth(handleAuthRefresh);

  return {
    loading,
    filing,
    setFiling,
    documents,
    documentsLoading,
    dependents,
    setDependents,
    fetchData,
    fetchDocuments,
    fetchDependentsData,
    handleDocumentUpdated,
  };
}
